import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner'
import React, { ComponentPropsWithoutRef, ReactNode } from 'react'

import { SContainer as SHeaderContainer } from '../header'
import DropdownMenu from '../../common/components/DropdownMenu'
import { ButtonContainer } from '../../common/styles/Button'

import {
    SDisabledOverlay,
    SLoadingIcon,
    SWidget,
    SWidgetActionButtons,
    SWidgetHeader,
    WidgetHeaderText,
} from './Widget.style'

type WidgetTitleOwnProps = {
    title: React.ReactNode
    loadingStatus?: boolean
    loading?: boolean
    headerInfoIcon?: React.ReactNode
    headerText?: string
    'data-testid'?: string
}

type WidgetTitleProps = WidgetTitleOwnProps & Omit<ComponentPropsWithoutRef<'div'>, keyof WidgetTitleOwnProps>

type WidgetLayoutOwnProps = {
    children: React.ReactNode
    actionButtons?: React.ReactElement | Array<React.ReactElement>
    title?: React.ReactNode
    headerText?: React.ReactNode
    loadingStatus?: boolean
    isLoading?: boolean
    headerInfoIcon?: React.ReactNode | null
    headerButtons?: ReactNode
    contextMenuItems?: React.ReactNode
    disabled?: boolean
    'data-testid'?: string
}

export type WidgetLayoutProps = WidgetLayoutOwnProps & Omit<ComponentPropsWithoutRef<'div'>, keyof WidgetLayoutOwnProps>

export const WidgetTitle = ({
    title,
    loadingStatus,
    headerInfoIcon,
    ...props
}: WidgetTitleProps): React.ReactElement => (
    <SHeaderContainer size="md" data-testid={`${props['data-testid'] ?? 'widget'}-title`}>
        {title}
        {headerInfoIcon}
        {loadingStatus && <SLoadingIcon icon={faSpinner} pulse data-testid="spinner" />}
    </SHeaderContainer>
)

export function WidgetLayout({
    children,
    actionButtons,
    title,
    loadingStatus,
    isLoading,
    headerText,
    headerInfoIcon,
    headerButtons,
    contextMenuItems,
    disabled,
    ...props
}: WidgetLayoutProps): React.ReactElement {
    return (
        <SWidget {...props}>
            {
                <SWidgetHeader>
                    {title && (
                        <WidgetTitle
                            title={title}
                            loadingStatus={loadingStatus}
                            data-testid={(props['data-testid'] as string) ?? 'widget'}
                            headerInfoIcon={headerInfoIcon}
                        />
                    )}

                    {headerText && <WidgetHeaderText data-testid="header-text">{headerText}</WidgetHeaderText>}
                    {(headerButtons || contextMenuItems) && (
                        <ButtonContainer>
                            {headerButtons}
                            {contextMenuItems && <DropdownMenu>{contextMenuItems}</DropdownMenu>}
                        </ButtonContainer>
                    )}
                </SWidgetHeader>
            }

            {isLoading ? <div className="shimmer-container shimmer-small" data-testid="loading" /> : children}
            {actionButtons && <SWidgetActionButtons>{actionButtons}</SWidgetActionButtons>}
            {disabled && <SDisabledOverlay data-testid="disabledOverlay" />}
        </SWidget>
    )
}

export default WidgetLayout
